"use client";

import { Box, Flex } from "@chakra-ui/react";
import { useEffect } from "react";
import type { GetPolicyResponse } from "@/openapi/generated";
import type { ContentSettings } from "@/lib/utils/content-settings/content-settings-defaults";
import { MicrositeListingHero } from "@/components/shared/MicrositeListingHero";
import { PublicEntrance } from "@/components/shared/motion";
import { publicScrollMarginBelowNavbar } from "@/lib/layout/public-sticky-top";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Text } from "@/components/ui";
import { ContentSettingsText } from "@/lib/utils/content-settings/ContentSettingsText";
import {
  WaiversList,
  TermsTableOfContents,
  scrollToTermsWaiver,
  waiverAnchorId,
} from "./components";

export interface TermsPageViewProps {
  waivers: GetPolicyResponse[];
  contentSettings: Pick<
    ContentSettings,
    | "termsSubtitle"
    | "termsTitle"
    | "termsDescription"
    | "termsSignUpPrompt"
    | "termsFooterNote"
  >;
  showStatus?: boolean;
  tenantEmail?: string | null;
  studioName?: string | null;
}

export const TermsPageView = ({
  waivers,
  contentSettings,
  showStatus = false,
  tenantEmail,
  studioName,
}: TermsPageViewProps) => {
  const theme = useThemeTokens();
  const isPublicLayout = !showStatus;
  const documentLabel =
    waivers.length === 1 ? "1 document" : `${waivers.length} documents`;

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;

    const hasMatchingWaiver = waivers.some(
      (waiver) => waiverAnchorId(waiver.policyId) === hash,
    );
    if (!hasMatchingWaiver) return;

    const timeoutId = window.setTimeout(() => scrollToTermsWaiver(hash), 0);
    return () => window.clearTimeout(timeoutId);
  }, [waivers]);

  return (
    <Flex
      direction="column"
      gap={{ base: 6, md: 8 }}
      maxW={isPublicLayout ? "720px" : "1044px"}
      mx="auto"
      w="100%"
    >
      {isPublicLayout ? (
        <MicrositeListingHero
          subtitle={contentSettings.termsSubtitle}
          title={contentSettings.termsTitle}
          description={contentSettings.termsDescription}
          studioName={studioName}
          tenantEmail={tenantEmail}
        />
      ) : (
        <Box>
          <Text
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="0.18em"
            color={theme.text.tertiary}
            mb={2}
          >
            {contentSettings.termsSubtitle}
          </Text>
          <Text
            as="h1"
            fontSize={{ base: "2xl", md: "3xl" }}
            fontWeight="600"
            letterSpacing="-0.03em"
            color={theme.text.primary}
            mb={3}
            lineHeight="1.2"
          >
            {contentSettings.termsTitle}
          </Text>
          <Text
            variant="secondary"
            fontSize={{ base: "sm", md: "md" }}
            maxW="2xl"
            lineHeight="1.6"
          >
            {contentSettings.termsDescription}
          </Text>
        </Box>
      )}

      {!showStatus && contentSettings.termsSignUpPrompt ? (
        <PublicEntrance variant="subtle" delay={0.08}>
          <Box
            pt={{ base: 1, md: 2 }}
            borderTopWidth="1px"
            borderColor={theme.borderGlass.subtle}
          >
            <ContentSettingsText
              variant="secondary"
              fontSize="sm"
              lineHeight="1.65"
              tenantEmail={tenantEmail}
              studioName={studioName}
            >
              {contentSettings.termsSignUpPrompt}
            </ContentSettingsText>
          </Box>
        </PublicEntrance>
      ) : null}

      {!isPublicLayout ? (
        <TermsTableOfContents waivers={waivers} variant="portal" />
      ) : null}

      <PublicEntrance variant="group" delay={0.1} inView>
        <Box>
          {isPublicLayout ? (
            <Text
              variant="tertiary"
              fontSize="xs"
              textTransform="uppercase"
              letterSpacing="0.1em"
              fontWeight="500"
              mb={4}
            >
              {documentLabel}
            </Text>
          ) : null}
          <WaiversList
            waivers={waivers}
            showStatus={showStatus}
            variant={isPublicLayout ? "public" : "portal"}
            scrollMarginTop={publicScrollMarginBelowNavbar()}
          />
        </Box>

        {!showStatus && contentSettings.termsFooterNote ? (
          <Box
            pt={{ base: 2, md: 3 }}
            borderTopWidth="1px"
            borderColor={theme.borderGlass.subtle}
            mt={isPublicLayout ? 0 : undefined}
          >
            <ContentSettingsText
              variant="tertiary"
              fontSize="sm"
              lineHeight="1.65"
              tenantEmail={tenantEmail}
              studioName={studioName}
            >
              {contentSettings.termsFooterNote}
            </ContentSettingsText>
          </Box>
        ) : null}
      </PublicEntrance>
    </Flex>
  );
};
